import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Input,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Popover,
  Button,
  Avatar,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { usePagination } from '@mui/material';
import Layout from '../../../../components/Layout';
import AdminNavbar from '../../../../components/Admin/AdminNavbar/AdminNavbar';
import './Refund.css';
import { axiosInstance } from '../../../../utils/tokenUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import RefundTableSkeleton from '../../../../components/SkeletonEffect/RefundTableSkeleton/RefundTableSkeleton';
import { ArrowSquareLeft } from 'iconsax-react';
import { toast } from 'react-hot-toast';
import ColoredFilterIcon from '../../../../assets/Admin/RequestImages/colored-filter-icon.svg';
import FilterIcon from '../../../../assets/Admin/RequestImages/filter-icon.svg';

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const StatusArray = [
  { status: 'All' },
  { status: 'Pending' },
  { status: 'Approved' },
  { status: 'Rejected' },
  { status: 'Processing' },
  { status: 'Completed' },
  { status: 'Failed' },
];

const userRoleArray = [{ userRole: 'All' }, { userRole: 'Learner' }, { userRole: 'Instructor' }];

const Refund = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [refundData, setRefundData] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [openFilter, setOpenFilter] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [userRole, setUserRole] = useState('All');
  const [status, setStatus] = useState('All');
  const [country, setCountry] = useState('All');
  const [uiUserRole, setUiUserRole] = useState('All');
  const [uiStatus, setUiStatus] = useState('All');
  const [uiCountry, setUiCountry] = useState('All');
  const [countries, setCountries] = useState(['All']);
  const [studentId, setStudentId] = useState(null);
  const [isUrlParsed, setIsUrlParsed] = useState(false);

  const handleClickToOpenFilter = event => {
    setUiUserRole(userRole);
    setUiStatus(status);
    setUiCountry(country);
    setFilterAnchorEl(event.currentTarget);
    setOpenFilter(true);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
    setOpenFilter(false);
  };

  const handleApplyFilter = () => {
    setUserRole(uiUserRole);
    setStatus(uiStatus);
    setCountry(uiCountry);
    setCurrentPage(1);
    handleCloseFilter();
  };

  const handleClearAll = () => {
    setUiUserRole('All');
    setUiStatus('All');
    setUiCountry('All');
  };

  useEffect(() => {
    console.log('🔍 [Refund] URL changed:', location.search);
    setIsUrlParsed(false);
    const params = new URLSearchParams(location.search);
    const studentIdFromUrl = params.get('student_id');
    console.log('🔍 [Refund] student_id from URL:', studentIdFromUrl);
    if (studentIdFromUrl) {
      setStudentId(studentIdFromUrl);
      console.log('✅ [Refund] studentId state set to:', studentIdFromUrl);
    } else {
      console.log('ℹ️ [Refund] No student_id in URL, showing all refunds');
      setStudentId(null);
    }
    setIsUrlParsed(true);
    console.log('✅ [Refund] URL parsing complete');
  }, [location.search]);

  useEffect(() => {
    let isMounted = true;
    const fetchCountries = async () => {
      try {
        const res = await axiosInstance.get('/auth/countries');
        const names = (res?.data || []).map(item => item?.name).filter(Boolean);
        if (isMounted) {
          setCountries(['All', ...names]);
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
        if (isMounted) {
          setCountries(['All']);
        }
      }
    };
    fetchCountries();
    return () => {
      isMounted = false;
    };
  }, []);

  const toApiUserRole = value => {
    if (!value || value === 'All') return undefined;
    if (value === 'Learner') return 'student';
    if (value === 'Instructor') return 'teacher';
    return value.toLowerCase();
  };

  const toApiStatus = value => {
    if (!value || value === 'All') return undefined;
    const statusMap = {
      Pending: 'pending',
      Approved: 'approved',
      Completed: 'completed',
      Rejected: 'rejected',
      Processing: 'processing',
      Failed: 'failed',
    };
    return statusMap[value] || value.toLowerCase();
  };

  const fetchRefundData = useCallback(async () => {
    if (!isUrlParsed) {
      console.log('⏸️ [Refund] Skipping fetch - URL not parsed yet');
      return;
    }
    console.log('🔍 [Refund] fetchRefundData called');
    console.log('🔍 [Refund] Current studentId state:', studentId);
    setLoading(true);

    try {
      const queryParams = new URLSearchParams({
        page: String(currentPage),
        page_size: String(pageSize),
      });

      const apiUserRole = toApiUserRole(userRole);
      const apiStatus = toApiStatus(status);

      if (apiUserRole) queryParams.append('user_role', apiUserRole);
      if (apiStatus) queryParams.append('status', apiStatus);
      if (country && country !== 'All') queryParams.append('country', country.toLowerCase());
      if (debouncedSearchTerm?.trim())
        queryParams.append('search_query', debouncedSearchTerm.trim());
      if (studentId) {
        queryParams.append('student_id', studentId);
        console.log('✅ [Refund] Added student_id to query params:', studentId);
      }

      const apiUrl = `/admin/students/refund-requests?${queryParams.toString()}`;
      console.log('🔍 [Refund] API URL:', apiUrl);

      const response = await axiosInstance.get(apiUrl);

      console.log('✅ [Refund] API Response received');
      console.log('🔍 [Refund] Response count:', response.data?.count);
      console.log('🔍 [Refund] Response results length:', response.data?.results?.length);

      const records = response.data?.results || [];
      const count = response.data?.count || 0;

      const mapped = records.map(rec => {
        const userRoleMapped =
          rec.user_details?.user_role?.toLowerCase() === 'student' ||
          rec.user_details?.user_role?.toLowerCase() === 'learner'
            ? 'Learner'
            : rec.user_details?.user_role?.toLowerCase() === 'teacher' ||
                rec.user_details?.user_role?.toLowerCase() === 'instructor'
              ? 'Instructor'
              : rec.user_details?.user_role || 'Learner';

        return {
          id: rec.refund_id,
          refund_id: rec.refund_id,
          user_name: rec.user_details?.name || 'Unknown',
          user_profile: rec.user_details?.profile || null,
          user_role: userRoleMapped,
          created_at: rec.created_at,
          amount: rec.refund_amount || 0,
          refund_koins: rec.refund_koins || 0,
          payment_method: rec.payment_method || null,
          status: rec.status?.toLowerCase() || 'pending',
        };
      });

      setRefundData(mapped);
      setTotalCount(count);
      setTotalPages(Math.ceil(count / pageSize));
    } catch (error) {
      console.error('Error fetching refund data:', error);
      setRefundData([]);
      setTotalCount(0);
      setTotalPages(0);
      toast.error('Failed to load refund requests');
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    debouncedSearchTerm,
    userRole,
    status,
    country,
    studentId,
    isUrlParsed,
  ]);

  useEffect(() => {
    fetchRefundData();
  }, [fetchRefundData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handlePageSizeChange = event => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  const { items } = usePagination({
    count: totalPages,
    page: currentPage,
    onChange: handlePageChange,
  });

  const formatText = text => {
    if (!text) return text;
    return text
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const generatePaginationItems = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      if (start > 2) {
        pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages - 1) {
        pages.push('...');
      }

      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <Layout title="Refund">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        {studentId && (
          <Box sx={{ padding: '0.75rem 1rem 0 1rem' }}>
            <IconButton
              size="small"
              onClick={() => navigate(-1)}
              className="refund-back-icon-button"
            >
              <ArrowSquareLeft size="20" />
            </IconButton>
          </Box>
        )}
        <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
          <Box className="flex flex-col p-[1rem] gap-4">
            {studentId && (
              <Box className="refund-breadcrumb">
                <span className="refund-breadcrumb-prefix">
                  Payments {'>'} Refund {'>'} {'>'}
                </span>
                <span className="refund-breadcrumb-link">View All Refunds</span>
              </Box>
            )}
            <Box className="flex justify-between items-center">
              <Typography className="refund-title">
                {studentId ? 'View All Refunds' : 'Refund'}
              </Typography>
              <Box className="flex items-center gap-4 w-[40%]">
                <Input
                  sx={{
                    textDecoration: 'none',
                    border: '1px solid var(--Stroke, #D5DEE3)',
                    borderRadius: '56px',
                    '&:before, &:after': {
                      borderBottom: 'none !important',
                    },
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: 'none !important',
                    },
                    padding: '8px 10px',
                    '& .MuiInputBase-input': {
                      padding: '3px 5px 3px 5px',
                    },
                    width: '100%',
                    height: '40px',
                  }}
                  type="search"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  startAdornment={<SearchIcon className="search-icon-css" />}
                  disableUnderline
                />
                {openFilter ? (
                  <img
                    src={ColoredFilterIcon || '/placeholder.svg'}
                    alt="filter"
                    onClick={handleCloseFilter}
                    style={{ width: 25, height: 25, cursor: 'pointer' }}
                  />
                ) : (
                  <img
                    src={FilterIcon || '/placeholder.svg'}
                    alt="filter"
                    onClick={handleClickToOpenFilter}
                    style={{ width: 25, height: 25, cursor: 'pointer' }}
                  />
                )}
              </Box>
            </Box>
            <Popover
              open={openFilter}
              anchorEl={filterAnchorEl}
              onClose={handleCloseFilter}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ className: 'refund-filter-modal-container' }}
            >
              <Box className="refund-filter-modal-header">
                <Typography className="refund-filter-modal-title">Filters</Typography>
                <CloseIcon
                  onClick={handleCloseFilter}
                  sx={{
                    cursor: 'pointer',
                    color: '#949DA6',
                    fontSize: '20px',
                    '&:hover': { color: '#14171F' },
                  }}
                />
              </Box>
              <Box className="refund-filter-modal-content">
                <Box className="refund-filter-grid">
                  <Box className="refund-filter-item">
                    <label className="refund-filter-label">User Role</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={uiUserRole}
                        onChange={e => setUiUserRole(e.target.value)}
                        displayEmpty
                        className="refund-filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }}
                      >
                        {userRoleArray.map(option => (
                          <MenuItem
                            key={option.userRole}
                            value={option.userRole}
                            className="refund-dropdown-option"
                          >
                            {option.userRole}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box className="refund-filter-item">
                    <label className="refund-filter-label">Status</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={uiStatus}
                        onChange={e => setUiStatus(e.target.value)}
                        displayEmpty
                        className="refund-filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }}
                      >
                        {StatusArray.map(option => (
                          <MenuItem
                            key={option.status}
                            value={option.status}
                            className="refund-dropdown-option"
                          >
                            {option.status}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box className="refund-filter-item">
                    <label className="refund-filter-label">Country</label>
                    <FormControl sx={{ width: '100%' }}>
                      <Select
                        value={uiCountry}
                        onChange={e => setUiCountry(e.target.value)}
                        displayEmpty
                        className="refund-filter-input"
                        IconComponent={KeyboardArrowDownIcon}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }}
                      >
                        <MenuItem value="All" className="refund-dropdown-option">
                          All
                        </MenuItem>
                        {countries
                          .filter(c => c && c !== 'All')
                          .map(name => (
                            <MenuItem key={name} value={name} className="refund-dropdown-option">
                              {name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Box>
              <Box className="refund-filter-buttons-container">
                <Button
                  variant="outlined"
                  onClick={handleClearAll}
                  className="refund-filter-clear-button"
                >
                  Clear All
                </Button>
                <Button
                  variant="contained"
                  onClick={handleApplyFilter}
                  className="refund-filter-apply-button"
                >
                  Apply
                </Button>
              </Box>
            </Popover>

            <Box className="refund-table-container">
              {loading ? (
                <RefundTableSkeleton />
              ) : (
                <table className="refund-table">
                  <thead>
                    <tr>
                      <th className="refund-th">Refund ID</th>
                      <th className="refund-th">User Name</th>
                      <th className="refund-th">User Role</th>
                      <th className="refund-th">Created On</th>
                      <th className="refund-th">Amount (Riyals)</th>
                      <th className="refund-th">Koins</th>
                      <th className="refund-th">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {totalCount === 0 ? (
                      <tr>
                        <td colSpan={7} className="refund-td">
                          <Box className="refund-empty-state">
                            <Typography className="refund-empty-text">
                              No refund records found.
                            </Typography>
                          </Box>
                        </td>
                      </tr>
                    ) : (
                      refundData.map(refund => (
                        <tr
                          key={refund.id}
                          className="refund-row"
                          onClick={() => navigate(`/admin/payments/refund-details/${refund.id}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="refund-td">{refund.refund_id || refund.id || '-'}</td>
                          <td className="refund-td">
                            <Box className="flex items-center gap-3">
                              <Avatar
                                src={refund.user_profile}
                                sx={{ width: 25, height: 25 }}
                                alt={refund.user_name}
                              >
                                {refund.user_name?.charAt(0)?.toUpperCase() || 'U'}
                              </Avatar>
                              <Typography>{refund.user_name || '-'}</Typography>
                            </Box>
                          </td>
                          <td className="refund-td">
                            <Box
                              sx={{
                                height: '30px',
                                fontSize: '12px',
                                fontFamily: 'Poppins',
                                fontWeight: '400',
                                paddingLeft: '10px',
                                paddingRight: '10px',
                                paddingTop: '4px',
                                paddingBottom: '4px',
                                borderRadius: 20,
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                display: 'inline-flex',
                                backgroundColor:
                                  refund.user_role === 'Learner' || refund.user_role === 'student'
                                    ? '#F0EFFF'
                                    : '#FFF3CE',
                                color:
                                  refund.user_role === 'Learner' || refund.user_role === 'student'
                                    ? '#6B60E7'
                                    : '#BC8D00',
                              }}
                            >
                              <Typography>
                                {refund.user_role === 'student'
                                  ? 'Learner'
                                  : refund.user_role === 'teacher'
                                    ? 'Instructor'
                                    : formatText(refund.user_role)}
                              </Typography>
                            </Box>
                          </td>
                          <td className="refund-td">
                            {refund.created_at
                              ? new Date(refund.created_at).toLocaleDateString()
                              : '-'}
                          </td>
                          <td className="refund-td">{refund.amount || '-'}</td>
                          <td className="refund-td">{refund.refund_koins || '-'}</td>
                          <td className="refund-td">
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                borderRadius: '20px',
                                border: `1px solid ${
                                  refund.status === 'pending'
                                    ? '#E0AD14'
                                    : refund.status === 'completed' || refund.status === 'approved'
                                      ? '#14B334'
                                      : refund.status === 'processing'
                                        ? '#E0AD14'
                                        : refund.status === 'rejected' || refund.status === 'failed'
                                          ? '#E14949'
                                          : '#949DA6'
                                }`,
                                backgroundColor:
                                  refund.status === 'pending'
                                    ? '#FFFCF2'
                                    : refund.status === 'completed' || refund.status === 'approved'
                                      ? '#F2FAF3'
                                      : refund.status === 'processing'
                                        ? '#FFFCF2'
                                        : refund.status === 'rejected' || refund.status === 'failed'
                                          ? '#FFF2F2'
                                          : '#F8F9FA',
                                color:
                                  refund.status === 'pending'
                                    ? '#E0AD14'
                                    : refund.status === 'completed' || refund.status === 'approved'
                                      ? '#14B334'
                                      : refund.status === 'processing'
                                        ? '#E0AD14'
                                        : refund.status === 'rejected' || refund.status === 'failed'
                                          ? '#E14949'
                                          : '#949DA6',
                                padding: '4px 12px',
                                maxWidth: 'fit-content',
                              }}
                            >
                              <Box
                                sx={{
                                  width: '6px',
                                  height: '6px',
                                  borderRadius: '50%',
                                  backgroundColor:
                                    refund.status === 'pending'
                                      ? '#E0AD14'
                                      : refund.status === 'completed' ||
                                          refund.status === 'approved'
                                        ? '#14B334'
                                        : refund.status === 'processing'
                                          ? '#E0AD14'
                                          : refund.status === 'rejected' ||
                                              refund.status === 'failed'
                                            ? '#E14949'
                                            : '#949DA6',
                                }}
                              />
                              <Typography
                                sx={{
                                  fontFamily: 'Poppins',
                                  fontSize: '14px',
                                  fontStyle: 'normal',
                                  fontWeight: 400,
                                  lineHeight: 'normal',
                                  whiteSpace: 'nowrap',
                                  textTransform: 'capitalize',
                                }}
                              >
                                {formatText(refund.status)}
                              </Typography>
                            </Box>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </Box>

            {totalCount > 0 && (
              <Box className="refund-pagination-container">
                <Box className="refund-pagination-box">
                  <Typography className="refund-pagination-text">Showing</Typography>
                  <Select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    className="refund-pagination-select"
                    sx={{
                      height: '30px',
                      minWidth: '60px',
                      margin: '0 4px',
                      '& .MuiSelect-select': {
                        padding: '6px 24px 6px 8px',
                        textAlign: 'center',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '& .MuiSelect-icon': {
                        right: '4px',
                        color: '#666',
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={15}>15</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={30}>30</MenuItem>
                  </Select>
                  <Typography className="refund-pagination-text">
                    of {totalCount} Refunds
                  </Typography>
                </Box>

                <nav>
                  <ul className="refund-pagination-list">
                    <li>
                      <button
                        type="button"
                        className={`refund-pagination-btn ${currentPage === 1 ? 'refund-pagination-btn-disabled' : ''}`}
                        onClick={() => handlePageChange(null, currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>

                    {generatePaginationItems().map((item, index) => (
                      <li key={index}>
                        {item === '...' ? (
                          <span className="refund-pagination-ellipsis">…</span>
                        ) : (
                          <button
                            type="button"
                            className={`refund-pagination-btn ${currentPage === item ? 'refund-pagination-btn-selected' : ''}`}
                            onClick={() => handlePageChange(null, item)}
                          >
                            {item}
                          </button>
                        )}
                      </li>
                    ))}

                    <li>
                      <button
                        type="button"
                        className={`refund-pagination-btn ${currentPage === totalPages ? 'refund-pagination-btn-disabled' : ''}`}
                        onClick={() => handlePageChange(null, currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default Refund;
